import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../../../../firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';

// IMPORTAMOS LA UTILIDAD DE BORRADO PROFUNDO
import { deleteRemesaComisionFull } from '../../../../firebase/firebaseUtils';

import ModalConfirmacion from '../../../ui/ModalConfirmacion';

/**
 * RemesasTab - Gestión de lotes de liquidaciones importadas.
 * Refacturado: Utiliza React Portals para proyectar el modal al body, evitando recortes de CSS.
 */
const RemesasTab = () => {
    const [remesas, setRemesas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [remesaToDelete, setRemesaToDelete] = useState(null);

    const fetchRemesas = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'public/data/remesas'), orderBy('fecha', 'desc'));
            const s = await getDocs(q);
            // Mapeamos docId (el ID físico) e id (el batchId grabado dentro)
            setRemesas(s.docs.map(d => ({ docId: d.id, ...d.data() })));
        } catch (e) { 
            console.error("Error cargando remesas:", e); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { fetchRemesas(); }, []);

    const handleDeleteRemesa = async () => {
        if (!remesaToDelete) return;
        setLoading(true);
        try {
            // Utilizamos la función del archivo central de utilidades
            // remesaToDelete.id es el batchId grabado en las liquidaciones
            // remesaToDelete.docId es el ID del documento en la carpeta 'remesas'
            await deleteRemesaComisionFull(remesaToDelete.id, remesaToDelete.docId);
            
            fetchRemesas();
        } catch (e) { 
            console.error("Error al eliminar remesa:", e); 
        } finally { 
            setLoading(false); 
            setRemesaToDelete(null); 
        }
    };

    // Helper para el estilo del Ramo
    const getRamoBadge = (tipo) => {
        if (tipo === 'ART') return 'bg-orange-100 text-orange-700 border-orange-200';
        return 'bg-cyan-100 text-cyan-700 border-cyan-200';
    };

    return (
        <>
            {/* CONTENEDOR DE LA TABLA - Mantiene su overflow-hidden sin interferir con el modal */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden animate-fade-in text-left">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left border-collapse">
                        <thead className="bg-gray-50 text-gray-700 font-bold text-xs uppercase border-b border-gray-200 select-none">
                            <tr>
                                <th className="px-6 py-4">Fecha Carga</th>
                                <th className="px-6 py-4">Ramo</th>
                                <th className="px-6 py-4">Periodo</th>
                                <th className="px-6 py-4">Archivo Original</th>
                                <th className="px-6 py-4 text-center">Registros</th>
                                <th className="px-6 py-4 text-center">Acción</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading && remesas.length === 0 ? (
                                <tr><td colSpan="6" className="p-16 text-center font-bold text-gray-400 animate-pulse uppercase text-[10px] tracking-widest">Sincronizando Historial...</td></tr>
                            ) : remesas.length === 0 ? (
                                <tr><td colSpan="6" className="p-12 text-center text-gray-400 italic font-medium">No se registran archivos procesados.</td></tr>
                            ) : (
                                remesas.map(r => (
                                    <tr key={r.docId} className="hover:bg-slate-50 transition-colors group">
                                        <td className="px-6 py-4 font-mono text-gray-500 text-xs">
                                            {new Date(r.fecha).toLocaleString('es-AR')}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase border ${getRamoBadge(r.tipo)}`}>
                                                {r.tipo || 'PATRIMONIALES'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 font-black text-slate-800 uppercase tracking-tight">
                                            {r.periodo}
                                        </td>
                                        <td className="px-6 py-4 text-gray-500 italic max-w-xs truncate" title={r.nombreArchivo}>
                                            {r.nombreArchivo}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-black text-xs border border-blue-100">
                                                {r.registros}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <button 
                                                onClick={() => setRemesaToDelete(r)} 
                                                className="text-red-500 hover:text-red-700 font-black text-[10px] uppercase tracking-wider border border-red-100 hover:bg-red-50 px-4 py-1.5 rounded-lg transition-all active:scale-95 shadow-sm cursor-pointer"
                                            >
                                                Eliminar Lote
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                
                <style>{`
                    .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                    @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
                `}</style>
            </div>

            {/* MODAL DE CONFIRMACIÓN POR PORTAL - Montado directamente en la raíz de document.body */}
            {remesaToDelete && createPortal(
                <ModalConfirmacion 
                    show={!!remesaToDelete} 
                    onClose={() => setRemesaToDelete(null)} 
                    onConfirm={handleDeleteRemesa} 
                    title="Eliminar Lote de Liquidación" 
                    message={`¿Estás seguro de eliminar el lote del periodo ${remesaToDelete?.periodo} (${remesaToDelete?.tipo || 'PATRIMONIALES'})? Esta acción es irreversible y borrará todas las liquidaciones individuales vinculadas a este archivo de forma permanente.`} 
                />,
                document.body
            )}
        </>
    );
};

export default RemesasTab;